import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { v4 as uuid } from 'uuid';

import { messages } from '../../i18n';

import { resizeImage } from '../../utils';

import styles from './Avatar.module.scss';

const NUM_DEFAULT_BADGE_COLORS = 10;

const getRandomIndex = (name) => {

	let total = 0;

	if (name) {

		for (let i = 0, len = name.length; i < len; i++) {
			total += name.charCodeAt(i);
		}

	}

	return total % NUM_DEFAULT_BADGE_COLORS;

};

const propTypes = {

	/**
	 * The avatar's image URL.
	 */
	image: PropTypes.string,

	/**
	 * The avatar's title.
	 */
	title: PropTypes.string,

	/**
	 * The size of the avatar to render
	 */
	size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

	/**
	 * Flag indicating whether this user should
	 * be displayed as a speaker.
	 */
	speaker: PropTypes.bool,

	/**
	 * Flag to render the avatar image as a rounded circle.
	 */
	rounded: PropTypes.bool,

	/**
	 * The URL to redirect to when clicking on the avatar (if any).
	 */
	url: PropTypes.string,

	/**
	 * Classes to pass down to the avatar
	 */
	className: PropTypes.string,

	/**
	 * The avatar icon (instead of an image).
	 */
	icon: PropTypes.element,

	/**
	 * Callback to handle additional logic for viewing the user profile modal.
	 */
	onClick: PropTypes.func,

};

const BORDER_WIDTH = 3;

const defaultProps = {
	size: 'medium',
	rounded: false,
};

const Avatar = ({ image, title, size, rounded, speaker, url, className, icon, onClick }) => {

	const intl = useIntl();
	const textPathRef = useRef();

	const width = parseSize(size);

	// Handles any CSS overrides for the speaker label text
	useEffect(() => {

		if (textPathRef.current) {

			// Check if there is a CSS override defined for the speaker label
			const styles = window.getComputedStyle(textPathRef.current, '::before');
			const content = styles.getPropertyValue('content');

			// Update label text if the override exists
			if (content && content !== 'none') {
				textPathRef.current.textContent = content.replace(/["]+/g, '');
			}

			// Change text color from transparent to white to prevent
			// change from flashing
			textPathRef.current.setAttribute('fill', 'white');

		}

	}, []);

	const containerStyling = {
		width: `${width}px`,
		minWidth: `${width}px`,
		maxWidth: `${width}px`,
		height: `${width}px`,
		minHeight: `${width}px`,
		maxHeight: `${width}px`,
		fontSize: `${Math.max(Math.min(16, width / 2), width / 5)}px`,
		borderRadius: rounded ? '50%' : `${BORDER_WIDTH}px`,
	};

	const imageStyling = {
		border: 'inherit',
		width: 'inherit',
		minWidth: 'inherit',
		maxWidth: 'inherit',
		height: 'inherit',
		minHeight: 'inherit',
		maxHeight: 'inherit',
		lineHeight: `${width}px`,
		fontSize: `${Math.max(Math.min(16, width / 2), width / 5)}px`,
		borderRadius: rounded ? '50%' : `${BORDER_WIDTH}px`,
	};

	// Keep track of fallback styling in case the profile picture is missing
	let color = '';

	let text;

	// Check if there is an avatar image to show
	if (image) {

		// If so, resize the avatar image and set as the background image
		imageStyling.backgroundImage = `url("${resizeImage(image, 'width', width)}")`;

	} else {
		// Otherwise, use the fallback styling of a random bg color and text
		color = `tc-default-bg-color-${getRandomIndex(title)}`;
		text = title && title.charAt(0);
	}

	let squareSpeakerLabel;

	if (speaker && !rounded) {
		squareSpeakerLabel = (
			<div
				className={`speaker-badge-text-path ${styles['speaker-badge-square']}`}
				style={{
					fontSize: `${width / 15}px`,
					borderRadius: `${BORDER_WIDTH}px`
				}}>
				<span className="speaker-badge-text-label">
					{intl.formatMessage(messages.TC_COMPONENTS_USER_AVATAR_SPEAKER_BADGE_LABEL)}
				</span>
			</div>
		);
	}

	let avatar;


	if (url) {

		avatar = (
			<a
				className={`tc-avatar-container ${className || ''}`}
				href={url}
				title={title}
				target="_blank"
				rel="noopener noreferrer"
				style={containerStyling}
				onClick={onClick}>
				<div className={styles['img-container']}>
					<span
						className={`tc-avatar-img ${color}`}
						style={imageStyling}>
						{icon || text}
					</span>
					{speaker && !rounded && squareSpeakerLabel}
				</div>
			</a>
		);

	} else {

		avatar = (
			<div
				className={`tc-avatar-container ${className || ''}`}
				style={containerStyling}>
				<div className={styles['img-container']}>
					<span
						className={`tc-avatar-img ${color}`}
						style={imageStyling}
						title={title}>
						{icon || text}
					</span>
					{speaker && !rounded && squareSpeakerLabel}
				</div>
			</div>
		);

	}

	if (speaker && rounded) {

		// Draw speaker badge label for round avatars
		const radius = width / 2.0;
		const strokeWidth = width / 8.0;

		const curveId = uuid();

		return (
			<div
				className={styles['speaker-badge-container']}
				style={{ width: `${width}px`, height: `${width}px` }}>
				{avatar}
				<svg>
					<defs>
						<linearGradient
							id={`linear-gradient-${curveId}`}
							gradientTransform="rotate(-10)">
							<stop
								offset="0%"
								stopColor="var(--speaker-color, var(--primary-color))" />
							<stop
								offset="25%"
								stopColor="transparent" />
						</linearGradient>
					</defs>
					<path
						id={`curve-${curveId}`}
						fill="transparent"
						transform={`rotate(-30, ${radius} ${radius})`}
						style={{
							stroke: `url(#linear-gradient-${curveId})`,
							strokeWidth: `${strokeWidth}px`,
						}}
						d={`M ${(strokeWidth) / 2.0}, ${radius}
							a ${radius - strokeWidth},${radius - strokeWidth} 0 1,0 ${width - strokeWidth},0
						`}>
					</path>
					<text
						fill="white"
						fontSize={`${width / 15}px`}
						className={styles['speaker-badge-text']}>
						<textPath
							ref={textPathRef}
							id={`text-path-${curveId}`}
							className="speaker-badge-text-path"
							href={`#curve-${curveId}`}
							fill="transparent"
							startOffset="5%">
							{intl.formatMessage(messages.TC_COMPONENTS_USER_AVATAR_SPEAKER_BADGE_LABEL)}
						</textPath>
					</text>
				</svg>
			</div>
		);

	}

	return avatar;

};


/**
 * Parse size
 *
 * Helper function to determine the appropriate width of the avatar based
 * on either a string or numeric input.
 *
 * @function parseSize
 * @param    {string|integer}  size   Either a string or integer describing the desired size.
 * @returns  {string}          width  resulting width
 */
export function parseSize(size) {

	let width;

	if (Number.isInteger(parseInt(size, 10))) {

		width = parseInt(size, 10);

	} else {

		switch (size) {
		case 'small':

			width = 40;
			break;

		case 'medium':

			width = 150;
			break;

		case 'large':

			width = 300;
			break;

		default:

			width = 40;

		}

	}

	return width;

}

Avatar.propTypes = propTypes;
Avatar.defaultProps = defaultProps;

export default Avatar;
